import { useTranslation } from '@govard/i18n-core/react';
import { Alert, Button, Typography } from 'antd';
import { useState, type ReactElement } from 'react';

import { useScopeStore } from '../../../shared/scope/useCurrentScope.js';
import { useAcceptMyInvitationMutation, useMyPendingInvitations } from '../hooks/useTeam.js';

const { Text } = Typography;

/**
 * Железобетон: человек входит и сразу видит «вас позвали», без токена и без почты.
 * После приёма остаёмся в текущем workspace — данные не пропадут. Перейти можно
 * вручную через переключатель пространств.
 */
export function PendingInvitationsBanner(): ReactElement | null {
  const { t, locale } = useTranslation();
  const { data: pending } = useMyPendingInvitations();
  const acceptMut = useAcceptMyInvitationMutation();
  const switchWorkspace = useScopeStore((s) => s.switchWorkspace);
  const [accepted, setAccepted] = useState<
    Record<string, { workspaceId: string; workspaceName: string }>
  >({});

  const pendingList = pending ?? [];
  const hasPending = pendingList.length > 0;
  const hasAccepted = Object.keys(accepted).length > 0;
  if (!hasPending && !hasAccepted) return null;

  const stillPending = pendingList.filter((inv) => !accepted[inv.id]);
  const localeCode = locale === 'ru' ? 'ru-RU' : 'en-US';

  return (
    <div className="flex flex-col gap-3">
      {stillPending.map((inv) => {
        const expDate = inv.expiresAt ? new Date(inv.expiresAt) : null;
        const formattedDate =
          expDate && !Number.isNaN(expDate.getTime())
            ? expDate.toLocaleDateString(localeCode)
            : (inv.expiresAt ?? '—');

        return (
          <Alert
            key={inv.id}
            type="info"
            showIcon
            title={t('crm.pending_invitations.invited_message', {
              workspace: inv.workspaceName,
              role: inv.role,
            })}
            description={
              <div className="flex flex-col gap-2">
                <Text type="secondary" style={{ fontSize: 13 }}>
                  {t('crm.pending_invitations.expires_info', {
                    email: inv.email,
                    date: formattedDate,
                  })}
                </Text>
                <div className="flex gap-2">
                  <Button
                    type="primary"
                    size="small"
                    loading={acceptMut.isPending && acceptMut.variables === inv.id}
                    onClick={async () => {
                      const res = await acceptMut.mutateAsync(inv.id);
                      setAccepted((m) => ({
                        ...m,
                        [inv.id]: {
                          workspaceId: res.workspaceId,
                          workspaceName: inv.workspaceName,
                        },
                      }));
                    }}
                  >
                    {t('crm.invite.accept')}
                  </Button>
                </div>
                {acceptMut.isError && acceptMut.variables === inv.id ? (
                  <Text type="danger" style={{ fontSize: 12 }}>
                    {(acceptMut.error as Error).message}
                  </Text>
                ) : null}
              </div>
            }
          />
        );
      })}
      {Object.entries(accepted).map(([id, info]) => (
        <Alert
          key={`accepted-${id}`}
          type="success"
          showIcon
          title={t('crm.pending_invitations.accepted_message', {
            workspace: info.workspaceName,
          })}
          description={
            <Button size="small" onClick={() => void switchWorkspace(info.workspaceId)}>
              {t('crm.pending_invitations.switch_workspace')}
            </Button>
          }
        />
      ))}
    </div>
  );
}
